/**
 * Copyright (c) 2026 Briq-n-Braq LLC
 * All rights reserved.
 *
 * Proprietary and confidential software.
 * Unauthorized copying, modification, distribution, or use
 * of this software, in whole or in part, is strictly prohibited.
 *
 * KNX Supply Portal — portal.briq-n-braq.com
 * KNX Certified Professional | Partner No. 20248
 */

const PSU_RATINGS = [160, 320, 640];
const MAX_PER_SEGMENT = 64;
const MAX_PER_LINE = 255;
const PSU_WARN_RATIO = 0.75;

// Smallest PSU whose 75% usable capacity covers the load; beyond the largest, add more of them.
function selectPSU(totalLoad) {
  const maxRating = PSU_RATINGS[PSU_RATINGS.length - 1];
  const rating = PSU_RATINGS.find((r) => totalLoad <= r * PSU_WARN_RATIO) || maxRating;
  const quantity = Math.max(1, Math.ceil(totalLoad / (maxRating * PSU_WARN_RATIO)));
  return { rating, quantity, capacity: rating * quantity };
}

function calculatePSU(deviceCount, mAperDevice = 5) {
  const totalLoad = deviceCount * mAperDevice;
  const { rating, quantity, capacity } = selectPSU(totalLoad);
  let warning = null;
  if (totalLoad > capacity * PSU_WARN_RATIO) {
    warning = `PSU load is ${Math.round((totalLoad / capacity) * 100)}% of capacity (${totalLoad}mA of ${capacity}mA) - consider headroom`;
  }
  return { quantity, rating, totalLoad, warning };
}

function calculateLineCouplers(deviceCount) {
  if (deviceCount <= MAX_PER_SEGMENT) {
    return { needed: false, quantity: 0, reason: `${deviceCount} devices fit in a single line segment (max ${MAX_PER_SEGMENT})` };
  }
  const quantity = Math.ceil(deviceCount / MAX_PER_SEGMENT);
  return { needed: true, quantity, reason: `${deviceCount} devices exceed ${MAX_PER_SEGMENT} per segment; ${quantity} segments need line couplers/repeaters` };
}

function calculateAreaCouplers(floors) {
  if (floors <= 1) {
    return { needed: false, quantity: 0, reason: 'Single floor - no area coupler required' };
  }
  return { needed: true, quantity: floors, reason: `${floors} floors - one area coupler per floor` };
}

function validateTopology(deviceCount, floors, mALoad) {
  const warnings = [];
  if (deviceCount > MAX_PER_SEGMENT) {
    warnings.push(`${deviceCount} devices exceeds the ${MAX_PER_SEGMENT}-device segment limit; line couplers required`);
  }
  if (deviceCount > MAX_PER_LINE) {
    warnings.push(`${deviceCount} devices exceeds the ${MAX_PER_LINE}-device line limit; split across multiple lines/areas`);
  }
  const { capacity } = selectPSU(mALoad);
  if (mALoad > capacity * PSU_WARN_RATIO) {
    warnings.push(`PSU load ${mALoad}mA is ${Math.round((mALoad / capacity) * 100)}% of ${capacity}mA capacity (75% threshold)`);
  }
  if (floors > 1 && deviceCount > MAX_PER_SEGMENT * 4) {
    warnings.push(`${floors} floors with ${deviceCount} devices - review area/line assignment`);
  }
  return warnings;
}

// Device catalogue: label and bus current draw (mA) per device.
// `sensors` is the generic 5mA sensor kept for backwards compatibility.
const DEVICE_CATALOG = {
  pushButtons:    { name: 'Push Button Sensor', mA: 4 },
  touchPanels:    { name: 'Touch Panel', mA: 10 },
  multiSensors:   { name: 'Multifunction Sensor 9-in-1', mA: 8 },
  presenceSensors:{ name: 'Presence Sensor', mA: 5 },
  sensors:        { name: 'Sensor', mA: 5 },
  actuators:      { name: 'Switching Actuator', mA: 2 },
  dimmers:        { name: 'Dimmer Actuator', mA: 5 },
  blindActuators: { name: 'Blind Actuator', mA: 5 },
  ipRouters:      { name: 'IP Router/Gateway', mA: 12 },
};

function generateBOM(inputs) {
  const { floors = 1, devices = {} } = inputs || {};
  const items = [];
  let totalDevices = 0;
  let busLoad = 0;

  for (const key of Object.keys(DEVICE_CATALOG)) {
    const quantity = devices[key] || 0;
    if (quantity > 0) {
      const { name, mA } = DEVICE_CATALOG[key];
      items.push({ category: 'device', key, name, quantity, mA, notes: `${mA}mA each` });
      totalDevices += quantity;
      busLoad += quantity * mA;
    }
  }

  const psu = calculatePSU(totalDevices, totalDevices ? busLoad / totalDevices : 5);
  psu.totalLoad = busLoad;
  const lineCouplers = calculateLineCouplers(totalDevices);
  const areaCouplers = calculateAreaCouplers(floors);
  const couplers = { line: lineCouplers, area: areaCouplers };

  items.unshift({ category: 'power', key: 'psu', name: `KNX PSU ${psu.rating}mA`, quantity: psu.quantity, notes: `One per line segment; ${busLoad}mA total bus load` });
  if (lineCouplers.needed) {
    items.push({ category: 'coupler', key: 'lineCoupler', name: 'Line Coupler', quantity: lineCouplers.quantity, notes: lineCouplers.reason });
  }
  if (areaCouplers.needed) {
    items.push({ category: 'coupler', key: 'areaCoupler', name: 'Area Coupler', quantity: areaCouplers.quantity, notes: areaCouplers.reason });
  }

  const warnings = validateTopology(totalDevices, floors, busLoad);
  return { items, totalDevices, busLoad, psu, couplers, warnings };
}

module.exports = { DEVICE_CATALOG, calculatePSU, calculateLineCouplers,
  calculateAreaCouplers, validateTopology, generateBOM };
